const API_BASE = '/api'

export interface UserInfo {
  sub: string
  email?: string      // canonical OAuth email
  username?: string   // provider display handle (GitHub login, etc.)
  name?: string
  avatar?: string
  hasProfile?: boolean
  role: string
}

// In-memory user store — populated after /auth/verify, never written to localStorage
let _currentUser: UserInfo | null = null

export function setCurrentUser(user: UserInfo | null): void {
  _currentUser = user
}

export function getUser(): UserInfo | null {
  return _currentUser
}

export function isGuest(): boolean {
  return _currentUser?.role === 'guest'
}

/**
 * UI-only authorization gate — decides who sees the full feature set vs. just the
 * public /auto-test page. This is a render-time check, NOT real authorization: the
 * server still enforces auth on every /api/* call. See the login note in App.tsx.
 *
 * Driven by VITE_AUTHORIZED_EMAIL_DOMAINS (comma-separated, e.g. "astemo.com,cypremos.com").
 * Mirrors the server's ALLOWED_EMAIL_DOMAINS convention: split on '@', exact domain match.
 * If the var is unset, every authenticated user is authorized (no hiding).
 */
export function isAuthorizedUser(): boolean {
  const allowed = (import.meta.env.VITE_AUTHORIZED_EMAIL_DOMAINS || '')
    .split(',')
    .map(d => d.trim())
    .filter(Boolean)
  if (allowed.length === 0) return true
  const domain = (_currentUser?.email ?? '').split('@')[1] ?? ''
  return allowed.includes(domain)
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

function getCsrfToken(): string | undefined {
  const match = document.cookie.split('; ').find(row => row.startsWith('csrf='))
  return match ? decodeURIComponent(match.split('=')[1]) : undefined
}

/**
 * Wrapper around fetch that:
 * 1. Prepends `/api` to relative paths
 * 2. Sends the session cookie automatically (credentials: 'include')
 * 3. Adds X-CSRF-Token header for state-changing requests (double-submit cookie pattern)
 * 4. On 401, calls logout and reloads (back to login)
 */
export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const url = path.startsWith('/') ? `${API_BASE}${path}` : `${API_BASE}/${path}`
  const method = (init?.method ?? 'GET').toUpperCase()

  const extraHeaders: Record<string, string> = {}
  if (!SAFE_METHODS.has(method)) {
    const csrfToken = getCsrfToken()
    if (csrfToken) extraHeaders['X-CSRF-Token'] = csrfToken
  }

  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: { ...init?.headers, ...extraHeaders },
  })

  if (response.status === 401) {
    _currentUser = null
    const csrfToken = getCsrfToken()
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
    }).catch(() => {})
    window.location.reload()
  }

  return response
}

/** Fire-and-forget feature tracking. Normalizes leading slashes. */
export function trackFeature(feature: string): void {
  const normalized = feature.replace(/^\/+/, '')
  if (!normalized) return
  apiFetch('/auth/track-feature', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ feature: normalized }),
  }).catch((err) => {
    console.warn('[trackFeature] failed:', err)
  })
}
